import React, { useState, useEffect } from 'react';
import { 
  getDwellTimeSettings, 
  updateDwellTimeSetting, 
  createDwellTimeSetting, 
  deleteDwellTimeSetting,
  getDwellTimeHistory 
} from '../services/durationCalculationService';

function DwellTimeSettings() {
  const [settings, setSettings] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [newSetting, setNewSetting] = useState({
    location_name: '',
    location_address: '',
    location_type: 'yard',
    default_dwell_minutes: 30,
    min_dwell_minutes: 10,
    max_dwell_minutes: 120,
    notes: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    const result = await getDwellTimeSettings();
    if (result.success) {
      setSettings(result.data || []);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    const result = await getDwellTimeHistory({ limit: 50 });
    if (result.success) {
      setHistory(result.data || []);
    }
  };

  const handleUpdate = async (id, updates) => {
    const result = await updateDwellTimeSetting(id, updates);
    if (result.success) {
      setSettings(settings.map(s => s.id === id ? result.data : s));
      setEditingId(null);
    } else {
      setError(result.error);
    }
  };

  const handleCreate = async () => {
    const result = await createDwellTimeSetting(newSetting);
    if (result.success) {
      setSettings([...settings, result.data]);
      setShowAddForm(false);
      setNewSetting({
        location_name: '',
        location_address: '',
        location_type: 'yard',
        default_dwell_minutes: 30,
        min_dwell_minutes: 10,
        max_dwell_minutes: 120,
        notes: ''
      });
    } else {
      setError(result.error);
    }
  };

  const handleDelete = async (id) => {
    if (deleteConfirmId !== id) { setDeleteConfirmId(id); return; }
    setDeleteConfirmId(null);
    
    const result = await deleteDwellTimeSetting(id);
    if (result.success) {
      setSettings(settings.filter(s => s.id !== id));
    } else {
      setError(result.error);
    }
  };

  const toggleHistory = () => {
    if (!showHistory) {
      fetchHistory();
    }
    setShowHistory(!showHistory);
  };

  const locationTypes = [
    { value: 'yard', label: 'Yard' },
    { value: 'customer', label: 'Customer Site' },
    { value: 'fuel_station', label: 'Fuel Station' },
    { value: 'rest_stop', label: 'Rest Stop' },
    { value: 'other', label: 'Other' }
  ];

  const inputStyle = {
    padding: '0.5rem',
    backgroundColor: 'var(--surface-2)',
    border: '1px solid var(--border-default)',
    borderRadius: '4px',
    color: 'var(--text-primary)',
    fontSize: '0.9rem',
    width: '100%'
  };

  const buttonStyle = {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 'bold'
  };

  if (loading) {
    return <div style={{ color: 'var(--text-muted)', padding: '1rem' }}>Loading dwell time settings...</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ color: 'var(--accent-primary)', margin: 0 }}>Dwell Time Settings</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={toggleHistory}
            style={{ ...buttonStyle, backgroundColor: showHistory ? 'var(--color-danger)' : 'var(--accent-blue)', color: 'var(--text-primary)' }}
          >
            {showHistory ? 'Hide History' : 'View History'}
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{ ...buttonStyle, backgroundColor: 'var(--btn-primary-bg)', color: 'var(--text-primary)' }}
          >
            {showAddForm ? 'Cancel' : '+ Add Location'}
          </button>
        </div>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
        Configure default dwell times for different locations. The system will learn from actual driver app data 
        and automatically adjust estimates over time (AI-adjusted values appear after 5+ samples).
      </p>

      {error && (
        <div style={{ backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', padding: '0.75rem', borderRadius: '5px', marginBottom: '1rem' }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Add New Setting Form */}
      {showAddForm && (
        <div style={{ backgroundColor: 'var(--bg-primary)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
          <h4 style={{ color: 'var(--text-primary)', marginTop: 0 }}>Add New Location</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Location Name *</label>
              <input
                type="text"
                value={newSetting.location_name}
                onChange={(e) => setNewSetting({ ...newSetting, location_name: e.target.value })}
                placeholder="e.g., Main Yard, ABC Customer"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Location Type</label>
              <select
                value={newSetting.location_type}
                onChange={(e) => setNewSetting({ ...newSetting, location_type: e.target.value })}
                style={inputStyle}
              >
                {locationTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Address (for matching)</label>
              <input
                type="text"
                value={newSetting.location_address}
                onChange={(e) => setNewSetting({ ...newSetting, location_address: e.target.value })}
                placeholder="Full or partial address to match against stops"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Default Dwell (minutes)</label>
              <input
                type="number"
                value={newSetting.default_dwell_minutes}
                onChange={(e) => setNewSetting({ ...newSetting, default_dwell_minutes: parseInt(e.target.value) || 30 })}
                min="5"
                max="240"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Min/Max (minutes)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="number"
                  value={newSetting.min_dwell_minutes}
                  onChange={(e) => setNewSetting({ ...newSetting, min_dwell_minutes: parseInt(e.target.value) || 10 })}
                  placeholder="Min"
                  style={{ ...inputStyle, width: '50%' }}
                />
                <input
                  type="number"
                  value={newSetting.max_dwell_minutes}
                  onChange={(e) => setNewSetting({ ...newSetting, max_dwell_minutes: parseInt(e.target.value) || 120 })}
                  placeholder="Max"
                  style={{ ...inputStyle, width: '50%' }}
                />
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Notes</label>
              <input
                type="text"
                value={newSetting.notes}
                onChange={(e) => setNewSetting({ ...newSetting, notes: e.target.value })}
                placeholder="Optional notes about this location"
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleCreate}
              disabled={!newSetting.location_name}
              style={{ ...buttonStyle, backgroundColor: newSetting.location_name ? 'var(--btn-primary-bg)' : 'var(--border-default)', color: 'var(--text-primary)' }}
            >
              Save Location
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              style={{ ...buttonStyle, backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Settings Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--surface-2)' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Location</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Type</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--accent-primary)' }}>Default (min)</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--accent-primary)' }}>AI Adjusted</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--accent-primary)' }}>Samples</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--accent-primary)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {settings.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  No dwell time settings configured. Click "Add Location" to create one.
                </td>
              </tr>
            ) : (
              settings.map(setting => (
                <tr key={setting.id} style={{ borderBottom: '1px solid var(--border-default)' }}>
                  <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                    <div>{setting.location_name}</div>
                    {setting.location_address && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{setting.location_address.substring(0, 50)}...</div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>
                    {locationTypes.find(t => t.value === setting.location_type)?.label || setting.location_type}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    {editingId === setting.id ? (
                      <input
                        type="number"
                        defaultValue={setting.default_dwell_minutes}
                        onBlur={(e) => handleUpdate(setting.id, { default_dwell_minutes: parseInt(e.target.value) })}
                        style={{ ...inputStyle, width: '60px', textAlign: 'center' }}
                      />
                    ) : (
                      <span style={{ color: 'var(--text-primary)' }}>{setting.default_dwell_minutes}</span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    {setting.ai_adjusted_dwell_minutes ? (
                      <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>{setting.ai_adjusted_dwell_minutes}</span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    {setting.sample_count || 0}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                      <button
                        onClick={() => setEditingId(editingId === setting.id ? null : setting.id)}
                        style={{ ...buttonStyle, backgroundColor: 'var(--accent-blue)', color: 'var(--text-primary)', padding: '0.25rem 0.5rem' }}
                      >
                        {editingId === setting.id ? 'Done' : 'Edit'}
                      </button>
                      {deleteConfirmId === setting.id ? (
                        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Sure?</span>
                          <button onClick={() => handleDelete(setting.id)} style={{ ...buttonStyle, backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}>Yes</button>
                          <button onClick={() => setDeleteConfirmId(null)} style={{ ...buttonStyle, backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}>No</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirmId(setting.id)} style={{ ...buttonStyle, backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', padding: '0.25rem 0.5rem' }}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* History Section */}
      {showHistory && (
        <div style={{ marginTop: '2rem' }}>
          <h4 style={{ color: 'var(--accent-primary)' }}>Recent Dwell Time History</h4>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            Actual dwell times recorded from driver app. These are used to calculate AI-adjusted estimates.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--surface-2)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', color: 'var(--accent-primary)', fontSize: '0.85rem' }}>Date</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', color: 'var(--accent-primary)', fontSize: '0.85rem' }}>Location</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', color: 'var(--accent-primary)', fontSize: '0.85rem' }}>Expected</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', color: 'var(--accent-primary)', fontSize: '0.85rem' }}>Actual</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', color: 'var(--accent-primary)', fontSize: '0.85rem' }}>Variance</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                      No dwell time history recorded yet. Data will appear here as drivers complete stops.
                    </td>
                  </tr>
                ) : (
                  history.map(h => (
                    <tr key={h.id} style={{ borderBottom: '1px solid var(--border-default)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        {new Date(h.recorded_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                        {h.dwell_setting?.location_name || h.location_address?.substring(0, 30) || '-'}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        {h.expected_dwell_minutes || '-'} min
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                        {h.actual_dwell_minutes} min
                      </td>
                      <td style={{ 
                        padding: '0.5rem', 
                        textAlign: 'center', 
                        fontSize: '0.85rem',
                        color: h.variance_minutes > 10 ? 'var(--color-danger)' : h.variance_minutes < -10 ? 'var(--accent-primary)' : 'var(--text-muted)'
                      }}>
                        {h.variance_minutes > 0 ? '+' : ''}{h.variance_minutes} min
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default DwellTimeSettings;
